import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { ORDER_STATES } from '../assets/data/arrays';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface AuditHistoryItem {
  id: number;
  id_pedido: number;
  nombre_tienda: string;
  campo_modificado: string;
  valor_anterior: any;
  valor_nuevo: any;
  usuario_modificador: string;
  fecha_modificacion: string;
  ip_modificador: string | null;
  agente_usuario: string | null;
  datos_adicionales: string | Record<string, unknown> | null;
  estado_actual: string;
  usuario_nombre: string;
  usuario_apellido: string;
  usuario: {
    nombre: string;
    apellido: string;
  };
}
interface AuditHistoryProps {
  orderId: number;
  isVisible: boolean;
  onClose: () => void;
}

// Función exportada para hacer la consulta al endpoint
export const fetchAuditHistoryData = async (orderId: number) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await axiosInstance.get(`/audit/history/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        error: null
      };
    } else {
      return {
        success: false,
        data: [],
        error: 'Error al cargar el historial'
      };
    }
  } catch (err) {
    console.error('Error fetching audit history:', err);
    return {
      success: false,
      data: [],
      error: 'Error al cargar el historial de cambios'
    };
  }
};

const AuditHistory: React.FC<AuditHistoryProps> = ({ orderId, isVisible, onClose }) => {
  console.log('AuditHistory renderizado:', { orderId, isVisible, onClose });
  
  const [history, setHistory] = useState<AuditHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(`/audit/history/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setHistory(response.data.data);
      } else {
        
        setError('Error al cargar el historial');
      }
    } catch (err) {
      console.error('Error fetching audit history:', err);
      setError('Error al cargar el historial de cambios');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    console.log('useEffect AuditHistory:', { isVisible, orderId });
    if (isVisible && orderId) {
      console.log('Ejecutando fetchAuditHistory');
      fetchAuditHistory();
    }
  }, [isVisible, orderId, fetchAuditHistory]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusLabel = (status: string) => {
    const statusItem = ORDER_STATES.find(item => item.value === status);
    return statusItem ? statusItem.label : status;
  };

  const formatCampoModificadoLabel = (campo: string) =>
    campo === 'shipping' ? '"shipping"' : campo

  const formatTransactionId = (transactionId: any): string => {
    // Si es null, undefined, string vacío, número 0, o string "0", mostrar "Sin ID"
    if (transactionId === null || transactionId === undefined || transactionId === '' || transactionId === 0 || transactionId === '0') {
      return 'Sin ID';
    }
    // Convertir a string si es número
    return String(transactionId);
  };

  const normalizeDatosAdicionales = (
    raw: string | Record<string, unknown> | null | undefined
  ): Record<string, unknown> | null => {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t === '') return null;
      try {
        const parsed = JSON.parse(t);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
        return null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const DATOS_ADICIONALES_FIELD_ORDER = [
    'timestamp',
    'tipo',
    'payment_id',
    'estado_anterior',
    'estado_nuevo'
  ];
  const HIDDEN_DATOS_ADICIONALES_FIELDS = new Set([
    'timestamp',
    'payment_id',
    'estado_anterior',
    'estado_nuevo'
  ]);

  const sortDatosAdicionalesEntries = (entries: [string, unknown][]) => {
    return [...entries].sort(([a], [b]) => {
      const ia = DATOS_ADICIONALES_FIELD_ORDER.indexOf(a);
      const ib = DATOS_ADICIONALES_FIELD_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  const formatDatosAdicionalesValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    if (key === 'estado_anterior' || key === 'estado_nuevo') return getStatusLabel(String(value));
    return String(value);
  };

  const renderDatosAdicionales = (
    raw: string | Record<string, unknown> | null | undefined
  ): React.ReactNode => {
    const obj = normalizeDatosAdicionales(raw);
    if (obj) {
      const entries = Object.entries(obj).filter(
        ([k, v]) =>
          !HIDDEN_DATOS_ADICIONALES_FIELDS.has(k) &&
          v !== null &&
          v !== undefined &&
          String(v).trim() !== ''
      );
      if (entries.length === 0) return null;
      const mensajeEntry = entries.find(([k]) => k === 'mensaje');
      const restEntries = sortDatosAdicionalesEntries(
        entries.filter(([k]) => k !== 'mensaje')
      );
      return (
        <div
          className="mt-1.5 pt-1.5 border-t border-gray-200 text-[10px] leading-snug text-gray-500 space-y-0.5"
          role="region"
          aria-label="Datos adicionales del registro"
        >
          {mensajeEntry && (
            <div className="break-words">
              <span className="font-medium text-gray-600">mensaje: </span>
              <span>
                {typeof mensajeEntry[1] === 'object'
                  ? JSON.stringify(mensajeEntry[1])
                  : String(mensajeEntry[1])}
              </span>
            </div>
          )}
          {restEntries.map(([k, v]) => (
            <div key={k} className="break-words pl-1">
              <span className="font-medium text-gray-600">{k}: </span>
              <span>{formatDatosAdicionalesValue(k, v)}</span>
            </div>
          ))}
        </div>
      );
    }
    if (typeof raw === 'string' && raw.trim() !== '') {
      return (
        <div
          className="mt-1.5 pt-1.5 border-t border-gray-200 text-[10px] leading-snug text-gray-500 break-words"
          role="region"
          aria-label="Datos adicionales del registro"
        >
          <div className="break-words">{raw}</div>
        </div>
      );
    }
    return null;
  };

  const formatProducts = (products: any) => {
    if (!products) return null;
    if (typeof products === 'string') {
      try {
        products = JSON.parse(products);
      } catch {
        return null;
      }
    }
    if (Array.isArray(products)) {
      return products.map((product: any, index: number) => (
        <div key={index} className="mb-1 text-xs pl-2 border-l-2 border-gray-300">
          <span className="font-medium text-gray-700">{product.product_name || product.product_id}</span>
          <span className="text-gray-600"> - Cantidad: {product.quantity}</span>
          {product.price && (
            <span className="text-gray-600"> - Precio: ${parseFloat(product.price).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          )}
          {product.iva && (
            <span className="text-gray-500"> - IVA: {product.iva}%</span>
          )}
        </div>
      ));
    }
    return null;
  };

  const renderChanges = (item: AuditHistoryItem) => {
    const changes = [];
    
    // Caso especial: campo_modificado === "multiple"
    if (item.campo_modificado === 'multiple') {
      const valorAnterior = item.valor_anterior || {};
      const valorNuevo = item.valor_nuevo || {};
      
      // Cambio de estado dentro de "multiple"
      if (valorAnterior.status !== valorNuevo.status) {
        changes.push(
          <div key="status-multiple" className="mb-2">
            <span className="font-medium text-gray-700 text-xs">Estado: </span>
            <span className="text-yellow-600 text-xs">
              {valorAnterior.status ? getStatusLabel(valorAnterior.status) : 'N/A'}
            </span>
            <span className="mx-1 text-xs">→</span>
            <span className="text-green-600 font-medium text-xs">
              {valorNuevo.status ? getStatusLabel(valorNuevo.status) : 'N/A'}
            </span>
          </div>
        );
      }
      
      // Cambio de transaction_id dentro de "multiple"
      if (valorAnterior.transaction_id !== valorNuevo.transaction_id) {
        const oldValue = formatTransactionId(valorAnterior.transaction_id);
        const newValue = formatTransactionId(valorNuevo.transaction_id);
        
        // Solo mostrar si hay un cambio real (no ambos "Sin ID")
        if (oldValue !== newValue) {
          changes.push(
            <div key="transaction-multiple" className="mb-2">
              <span className="font-medium text-gray-700 text-xs">ID Transacción: </span>
              <span className="text-yellow-600 text-xs">
                {oldValue}
              </span>
              <span className="mx-1 text-xs">→</span>
              <span className="text-green-600 font-medium text-xs">
                {newValue}
              </span>
            </div>
          );
        }
      }
      
      // Productos en lineItems
      if (valorNuevo.lineItems && Array.isArray(valorNuevo.lineItems) && valorNuevo.lineItems.length > 0) {
        const productosAnteriores = valorAnterior.lineItems || [];
        const productosNuevos = valorNuevo.lineItems;
        
        if (JSON.stringify(productosAnteriores) !== JSON.stringify(productosNuevos)) {
          changes.push(
            <div key="lineItems-multiple" className="mb-2">
              <span className="font-medium text-gray-700 text-xs">Productos: </span>
              {productosAnteriores.length > 0 && (
                <div className="mt-1">
                  <div className="text-yellow-600 text-xs mb-1">Anterior:</div>
                  <div className="ml-2">{formatProducts(productosAnteriores)}</div>
                </div>
              )}
              <div className="mt-1">
                <div className="text-green-600 text-xs mb-1">Nuevo:</div>
                <div className="ml-2">{formatProducts(productosNuevos)}</div>
              </div>
            </div>
          );
        }
      }
      
      // Productos agregados
      if (valorNuevo.productosAgregados && Array.isArray(valorNuevo.productosAgregados) && valorNuevo.productosAgregados.length > 0) {
        changes.push(
          <div key="productosAgregados-multiple" className="mb-2">
            <span className="font-medium text-gray-700 text-xs">Productos Agregados: </span>
            <div className="mt-1 ml-2">
              {formatProducts(valorNuevo.productosAgregados)}
            </div>
          </div>
        );
      }
      
      // Si no se detectaron cambios específicos en "multiple", mostrar información genérica
      if (changes.length === 0) {
        changes.push(
          <div key="multiple-empty" className="text-gray-500 text-xs">
            Múltiples campos modificados (sin cambios específicos detectados)
          </div>
        );
      }
      
      return changes.length > 0 ? changes : (
        <div className="text-gray-500 text-xs">Sin cambios específicos</div>
      );
    }
    
    // Cambios de estado - usando la misma lógica que OrderForm.tsx
    if (item.campo_modificado === 'estado' && item.valor_anterior !== item.valor_nuevo) {
      changes.push(
        <div key="status" className="mb-1">
          <span className="font-medium text-gray-700 text-xs">Estado: </span>
          <span className="text-yellow-600 text-xs">
            {getStatusLabel(typeof item.valor_anterior === 'object' ? JSON.stringify(item.valor_anterior) : (item.valor_anterior || ''))}
          </span>
          <span className="mx-1 text-xs">→</span>
          <span className="text-green-600 font-medium text-xs">
            {getStatusLabel(typeof item.valor_nuevo === 'object' ? JSON.stringify(item.valor_nuevo) : (item.valor_nuevo || ''))}
          </span>
        </div>
      );
    }

    // Cambios en transaction_id - usando la misma lógica que OrderForm.tsx (solo si no es "multiple")
    if (item.campo_modificado !== 'multiple' && item.valor_anterior?.transaction_id !== item.valor_nuevo?.transaction_id) {
      const oldValue = formatTransactionId(item.valor_anterior?.transaction_id);
      const newValue = formatTransactionId(item.valor_nuevo?.transaction_id);
      
      // Solo mostrar si hay un cambio real (no ambos "Sin ID")
      if (oldValue !== newValue) {
        changes.push(
          <div key="transaction_id" className="mb-1">
            <span className="font-medium text-gray-700 text-xs">ID Transacción: </span>
            <span className="text-yellow-600 text-xs">
              {oldValue}
            </span>
            <span className="mx-1 text-xs">→</span>
            <span className="text-green-600 font-medium text-xs">
              {newValue}
            </span>
          </div>
        );
      }
    }

    // Cambios en productos - mostrar información de productos de forma legible
    if (item.campo_modificado === 'productos') {
      changes.push(
        <div key="products" className="mb-1">
          <span className="font-medium text-gray-700 text-xs">Productos: </span>
          <div className="mt-1">
            {item.valor_anterior && (
              <>
                <div className="text-yellow-600 text-xs mb-1">Anterior:</div>
                <div className="ml-2">{formatProducts(item.valor_anterior)}</div>
              </>
            )}
            {item.valor_nuevo && (
              <>
                <div className="text-green-600 text-xs mb-1 mt-2">Nuevo:</div>
                <div className="ml-2">{formatProducts(item.valor_nuevo)}</div>
              </>
            )}
            {!item.valor_anterior && !item.valor_nuevo && (
              <div className="text-gray-500 text-xs">Sin productos</div>
            )}
          </div>
        </div>
      );
    }

    // Si no se detectaron cambios específicos pero hay campo_modificado, mostrar cambio genérico
    if (changes.length === 0 && item.campo_modificado) {
      changes.push(
        <div key="generic" className="mb-1">
          <span className="font-medium text-gray-700 text-xs">{formatCampoModificadoLabel(item.campo_modificado)}: </span>
          <span className="text-yellow-600 text-xs">
            {typeof item.valor_anterior === 'object' && item.valor_anterior !== null 
              ? JSON.stringify(item.valor_anterior) 
              : (item.valor_anterior || 'N/A')}
          </span>
          <span className="mx-1 text-xs">→</span>
          <span className="text-green-600 font-medium text-xs">
            {typeof item.valor_nuevo === 'object' && item.valor_nuevo !== null
              ? JSON.stringify(item.valor_nuevo) 
              : (item.valor_nuevo || 'N/A')}
          </span>
        </div>
      );
    }

    return changes.length > 0 ? changes : (
      <div className="text-gray-500 text-xs">Sin cambios específicos</div>
    );
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-25 flex items-start justify-end z-50 pt-48 pr-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-80 max-h-96 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-blue-800">
            Historial de cambios - #{orderId}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700">
           <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-3 overflow-y-auto max-h-80">
          {loading ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-800"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-600 text-sm">
              {error}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              No hay historial disponible
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded p-2">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {item.usuario_nombre} {item.usuario_apellido}
                      </div>
                      <div className="text-xs text-gray-600">
                        {formatDate(item.fecha_modificacion)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        ID: {item.usuario_modificador}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded p-2">
                    <div className="mb-1">
                      <span className="font-medium text-gray-700 text-xs">Campo: </span>
                      <span className="text-blue-600 text-xs">{formatCampoModificadoLabel(item.campo_modificado)}</span>
                    </div>
                    
                    <div className="space-y-1">
                      {renderChanges(item)}
                    </div>
                    {renderDatosAdicionales(item.datos_adicionales)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditHistory; 